import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for error tracking and monitoring
 */
export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.MODE || 'development';

  // Only initialize Sentry if DSN is provided
  if (!dsn) {
    console.warn(
      'Sentry DSN not found. Error tracking is disabled. Set VITE_SENTRY_DSN in your environment variables.'
    );
    return;
  }

  Sentry.init({
    dsn,
    environment,

    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

    // Set `tracePropagationTargets` to control what URLs distributed tracing should trace
    tracePropagationTargets: ['localhost', /^https:\/\/.*\.domainboost\.com/],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: environment === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    // Additional options
    integrations: [
      // Enable Session Replay
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      // Browser tracing integration
      Sentry.browserTracingIntegration(),
    ],

    // Filter out errors we don't want to track
    beforeSend(event, hint) {
      // Don't send events in development unless explicitly enabled
      if (
        environment === 'development' &&
        !import.meta.env.VITE_SENTRY_ENABLED
      ) {
        return null;
      }

      // Filter out common browser extension errors
      const error = hint.originalException;
      if (error && typeof error === 'object' && 'message' in error) {
        const message = String(error.message);
        if (
          message.includes('chrome-extension://') ||
          message.includes('moz-extension://') ||
          message.includes('safari-extension://')
        ) {
          return null;
        }
      }

      return event;
    },
  });

  // Log initialization (using console.warn as it's allowed by ESLint)
  if (environment === 'development') {
    console.warn(`Sentry initialized for ${environment} environment`);
  }
};

/**
 * Set user context for error tracking
 */
export const setSentryUser = (user: {
  id: string;
  email?: string;
  username?: string;
}) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
  });
};

/**
 * Clear user context (e.g., on logout)
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
};

/**
 * Add custom context to error reports
 */
export const setSentryContext = (
  key: string,
  context: Record<string, unknown>
) => {
  Sentry.setContext(key, context);
};

/**
 * Manually capture an exception
 */
export const captureException = (
  error: Error,
  context?: Record<string, unknown>
) => {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value as Record<string, unknown>);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
};

/**
 * Manually capture a message
 */
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = 'info'
) => {
  Sentry.captureMessage(message, level);
};

/**
 * Add breadcrumb for debugging
 */
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb) => {
  Sentry.addBreadcrumb(breadcrumb);
};

export { Sentry };
